import React, { useState, useEffect } from 'react';
import { attendanceService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Users,
  UserCheck,
  Clock,
  MapPin,
  RefreshCw,
  Search,
  AlertTriangle,
} from 'lucide-react';

export default function TeamPage() {
  const [loading, setLoading] = useState(true);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTeamAttendance();
  }, []);

  const loadTeamAttendance = async () => {
    setLoading(true);
    try {
      const data = await attendanceService.getTodayAttendance();
      setTeamAttendance(data);
    } catch (error) {
      console.error('Failed to load team attendance:', error);
      toast.error('Failed to load team attendance');
    } finally {
      setLoading(false);
    }
  };

  const filteredTeam = teamAttendance.filter(member =>
    member.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const presentCount = teamAttendance.filter(m => 
    m.status === 'checked_in' || m.status === 'checked_out'
  ).length;
  
  const lateCount = teamAttendance.filter(m => m.is_late).length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'checked_in':
        return 'bg-blue-500';
      case 'checked_out':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Team Overview</h1>
          <p className="text-muted-foreground mt-1">
            Real-time view of your team's attendance
          </p>
        </div>
        <Button onClick={loadTeamAttendance} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Team</p>
                <p className="text-2xl font-bold">{teamAttendance.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/10 text-green-500">
                <UserCheck className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Present Today</p>
                <p className="text-2xl font-bold">{presentCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-orange-500/10 text-orange-500">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Late Arrivals</p>
                <p className="text-2xl font-bold">{lateCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="team-search"
        />
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
        {filteredTeam.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No team members found</p>
            </CardContent>
          </Card>
        ) : (
          filteredTeam.map((member) => (
            <Card key={member.id} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {member.employee_name?.split(' ').map(n => n[0]).join('') || '??'}
                        </AvatarFallback>
                      </Avatar>
                      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-background ${getStatusColor(member.status)}`} />
                    </div>
                    <div>
                      <p className="font-semibold">{member.employee_name}</p>
                      <p className="text-sm text-muted-foreground">{member.department}</p>
                    </div>
                  </div>
                  {member.is_late && (
                    <Badge className="badge-warning">Late</Badge>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Check In
                    </span>
                    <span className="font-mono">
                      {member.check_in_time 
                        ? format(new Date(member.check_in_time), 'HH:mm')
                        : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Check Out
                    </span>
                    <span className="font-mono">
                      {member.check_out_time 
                        ? format(new Date(member.check_out_time), 'HH:mm')
                        : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Location
                    </span>
                    <span className="text-right">
                      {member.location_name || member.location_id || '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Type
                    </span>
                    <Badge variant="outline" className="capitalize">
                      {member.attendance_type || 'N/A'}
                    </Badge>
                  </div>
                </div>

                {!member.is_within_geofence && (
                  <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                    Outside assigned geofence
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
